import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Boxes, Puzzle, Layers, Cpu, GitBranch, Gauge, Palette, Film, Music,
  Brain, Network, LayoutDashboard, ScrollText, Backpack, Hand, Zap, Copy, Check,
  ShieldCheck, AlertTriangle, FileCode2, BookOpen, CircleSlash,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

// Icon + accent per IR subsystem (Creator-Mode dark command-center palette).
const SUB_META = {
  entities: { icon: Boxes, color: "#7c3aed", label: "Entities" },
  components: { icon: Puzzle, color: "#3b9eff", label: "Components" },
  scenes: { icon: Layers, color: "#22c55e", label: "Scenes" },
  behaviours: { icon: GitBranch, color: "#f59e0b", label: "Behaviours" },
  logic: { icon: Cpu, color: "#ef4444", label: "Logic" },
  physics: { icon: Gauge, color: "#06b6d4", label: "Physics" },
  materials: { icon: Palette, color: "#ec4899", label: "Materials" },
  animations: { icon: Film, color: "#a855f7", label: "Animations" },
  audio: { icon: Music, color: "#14b8a6", label: "Audio" },
  ai: { icon: Brain, color: "#f43f5e", label: "AI" },
  networking: { icon: Network, color: "#0ea5e9", label: "Networking" },
  ui: { icon: LayoutDashboard, color: "#84cc16", label: "UI" },
  quests: { icon: ScrollText, color: "#eab308", label: "Quests" },
  inventory: { icon: Backpack, color: "#fb923c", label: "Inventory" },
  interactions: { icon: Hand, color: "#38bdf8", label: "Interactions" },
  triggers: { icon: Zap, color: "#facc15", label: "Triggers" },
};
const ORDER = Object.keys(SUB_META);

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch {} }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 hover:bg-white/10 transition-colors"
    >
      {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copied" : "Copy"}
    </button>
  );
}

export default function GameIRInspector() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);       // {ir, summary, validation}
  const [schema, setSchema] = useState(null);   // {ir_version, subsystems, schema}
  const [docs, setDocs] = useState("");
  const [active, setActive] = useState(null);    // subsystem key opened in the drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ir, sc] = await Promise.all([
        api.get(`/ir/games/${gameId}`),
        api.get(`/ir/schema`),
      ]);
      setData(ir.data);
      setSchema(sc.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load the Game IR.");
    } finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/ir/docs`).then((r) => setDocs(r.data?.markdown || "")).catch(() => {}); }, []);

  if (loading) return <Spinner />;
  if (!data) return (
    <div className="p-8 max-w-3xl mx-auto text-center text-white/70" data-testid="ir-error">
      <p>Game IR unavailable.</p>
      <Button variant="outline" onClick={() => navigate(-1)} className="mt-4 rounded-full"><ArrowLeft className="w-4 h-4" /> Back</Button>
    </div>
  );

  const { ir, summary, validation } = data;
  const counts = summary.counts || {};
  const irJson = JSON.stringify(ir, null, 2);

  return (
    <div className="min-h-screen bg-[#070912] text-white" data-testid="game-ir-inspector">
      <div className="max-w-6xl mx-auto p-5 lg:p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="ir-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FileCode2 className="w-5 h-5 text-[#7c3aed]" />
              <h1 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight">Game IR Inspector</h1>
              <span data-testid="ir-version-badge" className="rounded-full bg-[#7c3aed]/20 border border-[#7c3aed]/50 px-2.5 py-0.5 text-[11px] font-bold text-[#c4b5fd] font-mono">
                IR v{summary.ir_version}
              </span>
            </div>
            <p className="text-sm text-white/50 truncate">
              {summary.title} · <span className="font-mono text-white/70">{summary.template}</span> · {summary.dimension?.toUpperCase()} · {summary.genre}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {validation.valid ? (
              <span data-testid="ir-valid-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 px-3 py-1 text-xs font-bold text-emerald-300">
                <ShieldCheck className="w-3.5 h-3.5" /> Valid IR
              </span>
            ) : (
              <span data-testid="ir-invalid-badge" className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 border border-rose-500/40 px-3 py-1 text-xs font-bold text-rose-300">
                <AlertTriangle className="w-3.5 h-3.5" /> {validation.errors.length} error{validation.errors.length === 1 ? "" : "s"}
              </span>
            )}
            <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-mono text-white/60" data-testid="ir-total">{summary.total} nodes</span>
          </div>
        </div>

        {(validation.errors?.length > 0 || validation.warnings?.length > 0) && (
          <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="ir-validation">
            {validation.errors?.map((e, i) => (
              <p key={`e${i}`} className="text-xs text-rose-300 flex items-start gap-2 mb-1"><CircleSlash className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {e}</p>
            ))}
            {validation.warnings?.slice(0, 6).map((w, i) => (
              <p key={`w${i}`} className="text-xs text-amber-300/80 flex items-start gap-2 mb-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {w}</p>
            ))}
          </div>
        )}

        <Tabs defaultValue="overview">
          <TabsList className="bg-white/5 border border-white/10" data-testid="ir-tabs">
            <TabsTrigger value="overview" data-testid="ir-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="document" data-testid="ir-tab-document">Document</TabsTrigger>
            <TabsTrigger value="schema" data-testid="ir-tab-schema">Schema</TabsTrigger>
            <TabsTrigger value="docs" data-testid="ir-tab-docs">Architecture</TabsTrigger>
          </TabsList>

          {/* Overview — subsystem grid */}
          <TabsContent value="overview" className="mt-5">
            <p className="text-sm text-white/50 mb-4">
              The engine-agnostic representation of <span className="text-white/80">{summary.title}</span> across 16 subsystems. Tap a card to inspect its nodes.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="ir-subsystem-grid">
              {ORDER.map((key) => {
                const m = SUB_META[key];
                const Icon = m.icon;
                const n = counts[key] || 0;
                return (
                  <button
                    key={key}
                    data-testid={`ir-sub-${key}`}
                    onClick={() => setActive(active === key ? null : key)}
                    className={`group text-left rounded-2xl border p-4 transition-all ${active === key ? "border-white/30 bg-white/[0.06]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]"} ${n === 0 ? "opacity-55" : ""}`}
                    style={{ boxShadow: active === key ? `0 0 0 1px ${m.color}55` : "none" }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: `${m.color}22`, color: m.color }}>
                        <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                      </span>
                      <span className="font-mono text-lg font-bold" style={{ color: n ? m.color : "#5b6472" }} data-testid={`ir-count-${key}`}>{n}</span>
                    </div>
                    <div className="text-sm font-bold">{m.label}</div>
                    <div className="text-[11px] text-white/45 line-clamp-2 mt-0.5">{schema?.schema?.[key]?.desc}</div>
                  </button>
                );
              })}
            </div>

            {active && (
              <div className="mt-5 rounded-2xl border border-white/12 bg-white/[0.03] p-4" data-testid="ir-sub-detail">
                <div className="flex items-center gap-2 mb-3">
                  <span className="grid place-items-center w-7 h-7 rounded-lg" style={{ background: `${SUB_META[active].color}22`, color: SUB_META[active].color }}>
                    {React.createElement(SUB_META[active].icon, { style: { width: 16, height: 16 } })}
                  </span>
                  <h3 className="font-bold">{SUB_META[active].label}</h3>
                  <span className="ml-auto"><CopyBtn testid="ir-sub-copy" text={JSON.stringify(ir[active], null, 2)} /></span>
                </div>
                <pre className="text-[11px] leading-relaxed font-mono text-white/75 bg-black/40 rounded-xl p-3 overflow-auto max-h-[420px]" data-testid="ir-sub-json">
{JSON.stringify(ir[active], null, 2)}
                </pre>
              </div>
            )}
          </TabsContent>

          {/* Full document */}
          <TabsContent value="document" className="mt-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-bold text-sm">Full IR document</h3>
              <span className="ml-auto"><CopyBtn testid="ir-doc-copy" text={irJson} /></span>
            </div>
            <pre className="text-[11px] leading-relaxed font-mono text-white/75 bg-black/40 rounded-2xl border border-white/10 p-4 overflow-auto max-h-[70vh]" data-testid="ir-document-json">
{irJson}
            </pre>
          </TabsContent>

          {/* Schema reference */}
          <TabsContent value="schema" className="mt-5">
            <p className="text-sm text-white/50 mb-4">IR <span className="font-mono text-white/80">v{schema?.ir_version}</span> — {ORDER.length} subsystems.</p>
            <div className="space-y-2" data-testid="ir-schema-list">
              {ORDER.map((key) => {
                const spec = schema?.schema?.[key] || {};
                const m = SUB_META[key];
                return (
                  <div key={key} data-testid={`ir-schema-${key}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                    <div className="flex items-center gap-2 mb-1">
                      {React.createElement(m.icon, { style: { width: 15, height: 15, color: m.color } })}
                      <span className="font-mono text-sm font-bold">{key}</span>
                      <span className="text-[10px] uppercase tracking-wide text-white/40 border border-white/15 rounded px-1.5 py-0.5">{spec.collection}</span>
                    </div>
                    <p className="text-xs text-white/55">{spec.desc}</p>
                    {spec.fields && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {spec.fields.map((f) => <span key={f} className="font-mono text-[10px] rounded bg-white/5 border border-white/10 px-1.5 py-0.5 text-white/50">{f}</span>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Architecture docs */}
          <TabsContent value="docs" className="mt-5">
            <div className="flex items-center gap-2 mb-3 text-white/60 text-sm"><BookOpen className="w-4 h-4" /> Game IR architecture</div>
            <pre className="text-[12px] leading-relaxed font-mono text-white/70 bg-black/40 rounded-2xl border border-white/10 p-5 overflow-auto max-h-[70vh] whitespace-pre-wrap" data-testid="ir-docs-md">
{docs || "Loading documentation…"}
            </pre>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
